import Button from "@components/Button";
import { RequestErrorModal } from "@components/modals/error-modal";
import PasswordField from "@components/text-field/password-field";
import { css } from "@emotion/css";
import { Grid } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { userChangePassword } from "api/clients/clients.request";
import { AxiosError } from "axios";
import { Form, Formik } from "formik";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useState } from "react";
import { CPagesPath } from "shared/models/const/pages-path";
import { object, ref, string } from "yup";

interface IChangePasswordForm {
  onSubmitSuccess?: (data: any) => void;
}

export default function ChangePasswordForm({
  onSubmitSuccess,
}: IChangePasswordForm) {
  const t = useTranslations("ProfileChangePassword.form");
  const tErrors = useTranslations("error");

  const [responseErrors, setResponseErrors] = useState<AxiosError | null>(null);

  const validationSchema = object({
    current_password: string().required(tErrors("required")),
    new_password: string().required(tErrors("required")),
    new_password_confirmation: string()
      .required(tErrors("required"))
      .oneOf([ref("new_password"), null], tErrors("passwordMustMatch")),
  });

  const initialValues = {
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  };

  const { mutate, isLoading } = useMutation(userChangePassword, {
    onSuccess: (data) => {
      if (onSubmitSuccess) {
        onSubmitSuccess(data);
      }
    },
    onError: setResponseErrors,
  });

  const onSubmit = (values: any) => mutate(values);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {() => (
        <Form className={gridContainer}>
          <Grid container spacing={3}>
            <Grid item xs={12} margin="auto">
              <PasswordField
                name="current_password"
                label={t("field.current-password") + "*"}
              />
            </Grid>
            <Grid item xs={12} margin="auto">
              <PasswordField
                name="new_password"
                label={t("field.new-password") + "*"}
              />
            </Grid>
            <Grid item xs={12} margin="auto">
              <PasswordField
                name="new_password_confirmation"
                label={t("field.repeat-password") + "*"}
              />
            </Grid>
            <Grid item xs={12} my={3} className={formButtonsContainer}>
              <Button
                type="submit"
                thin_button
                fullWidth
                disabled={isLoading}
                loadingMessage={isLoading ? t("buttons.submitLoading") : null}
              >
                {t("buttons.submit")}
              </Button>
              <Link href={CPagesPath.es.profile}>
                <Button thin_button fullWidth bg="--color-brand-grey-secondary">
                  {t("buttons.undo")}
                </Button>
              </Link>
            </Grid>
          </Grid>
          <RequestErrorModal
            open={!!responseErrors}
            error={responseErrors}
            close={() => setResponseErrors(null)}
          />
        </Form>
      )}
    </Formik>
  );
}

const gridContainer = css`
  .MuiInputBase-root {
    background-color: white;
  }
`;

const formButtonsContainer = css`
  display: grid;
  justify-content: center;
  grid-template-columns: repeat(auto-fit, 280px);
  grid-gap: 10px;
  direction: rtl;
`;
